import { useMutation, useQueryClient } from '@tanstack/react-query';
import { demandaService } from '../../../../services/demanda';
import { receivedDemandsService } from '../../../../services/receivedDemands';
import type { Demanda } from '../../../../models';
import { demandQueryKeys } from './useDemandQuery';

/**
 * Mutation para solucionar demanda com optimistic update
 */
export const useSolveDemandMutation = (demandaId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { comentario: string; solucionarTudo: boolean; nota: number }) => {
      if (data.solucionarTudo) {
        return demandaService.solucionarTudo(demandaId, data.comentario, data.nota);
      }
      return demandaService.solucionar(demandaId, data.comentario, data.nota);
    },

    // Optimistic Update
    onMutate: async (data) => {
      // Cancelar queries em andamento para evitar sobrescrever o optimistic update
      await queryClient.cancelQueries({ queryKey: demandQueryKeys.detail(demandaId) });

      // Snapshot do valor anterior (para rollback em caso de erro)
      const previousDemanda = queryClient.getQueryData<Demanda>(
        demandQueryKeys.detail(demandaId)
      );

      // Atualização otimista
      queryClient.setQueryData<Demanda>(demandQueryKeys.detail(demandaId), (old) => {
        if (!old) return old;
        return {
          ...old,
          status: data.solucionarTudo ? 'SOLUCIONADA' : 'AGUARDANDOAUTORIZACAO',
        } as Demanda;
      });

      // Retornar contexto com snapshot para rollback
      return { previousDemanda };
    },

    // Em caso de sucesso, atualizar com dados reais do servidor
    onSuccess: () => {
      // Invalidar e refetch a demanda específica (sem await para não bloquear UI)
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      // Invalidar listas relacionadas
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.lists() });
    },

    // Em caso de erro, fazer rollback
    onError: (_error, _variables, context) => {
      if (context?.previousDemanda) {
        queryClient.setQueryData(demandQueryKeys.detail(demandaId), context.previousDemanda);
      }
    },
  });
};

/**
 * Mutation para atribuir demanda
 */
export const useAssignDemandMutation = (demandaId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { userId: number; comentario?: string }) => {
      return demandaService.atribuir(demandaId, [data.userId], data.comentario);
    },

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      const previousDemanda = queryClient.getQueryData<Demanda>(
        demandQueryKeys.detail(demandaId)
      );

      queryClient.setQueryData<Demanda>(demandQueryKeys.detail(demandaId), (old) => {
        if (!old) return old;
        return { ...old, status: 'ATRIBUIDA' } as Demanda;
      });

      return { previousDemanda };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.lists() });
      receivedDemandsService.invalidateCache('received');
      receivedDemandsService.invalidateCache('toAssign');
    },

    onError: (_error, _variables, context) => {
      if (context?.previousDemanda) {
        queryClient.setQueryData(demandQueryKeys.detail(demandaId), context.previousDemanda);
      }
    },
  });
};

/**
 * Mutation para puxar demanda para si
 */
export const usePullDemandMutation = (demandaId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => demandaService.puxar(demandaId),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      const previousDemanda = queryClient.getQueryData<Demanda>(
        demandQueryKeys.detail(demandaId)
      );

      queryClient.setQueryData<Demanda>(demandQueryKeys.detail(demandaId), (old) => {
        if (!old) return old;
        return { ...old, status: 'EMPROGRESSO' } as Demanda;
      });

      return { previousDemanda };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.lists() });
      receivedDemandsService.invalidateCache('received');
      receivedDemandsService.invalidateCache('toAssign');
    },

    onError: (_error, _variables, context) => {
      if (context?.previousDemanda) {
        queryClient.setQueryData(demandQueryKeys.detail(demandaId), context.previousDemanda);
      }
    },
  });
};

/**
 * Mutation para aceitar demanda
 */
export const useAcceptDemandMutation = (demandaId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (comentario?: string) => demandaService.aceitar(demandaId, comentario),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      const previousDemanda = queryClient.getQueryData<Demanda>(
        demandQueryKeys.detail(demandaId)
      );

      queryClient.setQueryData<Demanda>(demandQueryKeys.detail(demandaId), (old) => {
        if (!old) return old;
        return { ...old, status: 'EMPROGRESSO' } as Demanda;
      });

      return { previousDemanda };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.lists() });
      receivedDemandsService.invalidateCache('received');
      receivedDemandsService.invalidateCache('toAssign');
    },

    onError: (_error, _variables, context) => {
      if (context?.previousDemanda) {
        queryClient.setQueryData(demandQueryKeys.detail(demandaId), context.previousDemanda);
      }
    },
  });
};

/**
 * Mutation para finalizar demanda (aceitar entrega)
 */
export const useFinalizeDemandMutation = (demandaId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { comentario?: string; nota: number }) => 
      demandaService.finalizar(demandaId, data.comentario, data.nota),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      const previousDemanda = queryClient.getQueryData<Demanda>(
        demandQueryKeys.detail(demandaId)
      );

      queryClient.setQueryData<Demanda>(demandQueryKeys.detail(demandaId), (old) => {
        if (!old) return old;
        return { ...old, status: 'FINALIZADA' } as Demanda;
      });

      return { previousDemanda };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.lists() });
    },

    onError: (_error, _variables, context) => {
      if (context?.previousDemanda) {
        queryClient.setQueryData(demandQueryKeys.detail(demandaId), context.previousDemanda);
      }
    },
  });
};

/**
 * Mutation para recusar demanda
 */
export const useRejectDemandMutation = (demandaId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (comentario: string) => demandaService.recusar(demandaId, comentario),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      const previousDemanda = queryClient.getQueryData<Demanda>(
        demandQueryKeys.detail(demandaId)
      );

      queryClient.setQueryData<Demanda>(demandQueryKeys.detail(demandaId), (old) => {
        if (!old) return old;
        return { ...old, status: 'EMATRIBUICAO' } as Demanda;
      });

      return { previousDemanda };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.lists() });
    },

    onError: (_error, _variables, context) => {
      if (context?.previousDemanda) {
        queryClient.setQueryData(demandQueryKeys.detail(demandaId), context.previousDemanda);
      }
    },
  });
};

/**
 * Mutation para devolver demanda
 */
export const useReturnDemandMutation = (demandaId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (comentario: string) => demandaService.devolver(demandaId, comentario),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      const previousDemanda = queryClient.getQueryData<Demanda>(
        demandQueryKeys.detail(demandaId)
      );

      queryClient.setQueryData<Demanda>(demandQueryKeys.detail(demandaId), (old) => {
        if (!old) return old;
        return { ...old, status: 'DEVOLVIDA' } as Demanda;
      });

      return { previousDemanda };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.lists() });
    },

    onError: (_error, _variables, context) => {
      if (context?.previousDemanda) {
        queryClient.setQueryData(demandQueryKeys.detail(demandaId), context.previousDemanda);
      }
    },
  });
};

/**
 * Mutation para marcar demanda como errada
 */
export const useMarkWrongDemandMutation = (demandaId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (comentario?: string) => demandaService.errada(demandaId, comentario),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      const previousDemanda = queryClient.getQueryData<Demanda>(
        demandQueryKeys.detail(demandaId)
      );

      queryClient.setQueryData<Demanda>(demandQueryKeys.detail(demandaId), (old) => {
        if (!old) return old;
        return { ...old, status: 'ERRADA' } as Demanda;
      });

      return { previousDemanda };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.lists() });
    },

    onError: (_error, _variables, context) => {
      if (context?.previousDemanda) {
        queryClient.setQueryData(demandQueryKeys.detail(demandaId), context.previousDemanda);
      }
    },
  });
};

/**
 * Mutation para desfazer entrega
 */
export const useUndoDeliveryMutation = (demandaId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (comentario?: string) => demandaService.desfazer(demandaId, comentario),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      const previousDemanda = queryClient.getQueryData<Demanda>(
        demandQueryKeys.detail(demandaId)
      );

      queryClient.setQueryData<Demanda>(demandQueryKeys.detail(demandaId), (old) => {
        if (!old) return old;
        return { ...old, status: 'EMPROGRESSO' } as Demanda;
      });

      return { previousDemanda };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.lists() });
    },

    onError: (_error, _variables, context) => {
      if (context?.previousDemanda) {
        queryClient.setQueryData(demandQueryKeys.detail(demandaId), context.previousDemanda);
      }
    },
  });
};

/**
 * Mutation para remandar/reenviar demanda
 */
export const useRemandDemandMutation = (demandaId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      descricao?: string | undefined;
      sugestao_entrega?: string | undefined;
      template_respostas?: {
        template_id: string;
        template_versao: string;
        preenchido_em: string;
        respostas: Record<string, unknown>;
      } | undefined;
      comentario?: string | undefined;
    }) => demandaService.remandar(demandaId, data),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.lists() });
    },
  });
};

/**
 * Mutation para remover atribuído da demanda (apenas gestor)
 */
export const useRemoveAssignedMutation = (demandaId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) => demandaService.removerAtribuido(demandaId, userId),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      const previousDemanda = queryClient.getQueryData<Demanda>(
        demandQueryKeys.detail(demandaId)
      );
      return { previousDemanda };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.lists() });
    },

    onError: (_error, _variables, context) => {
      if (context?.previousDemanda) {
        queryClient.setQueryData(demandQueryKeys.detail(demandaId), context.previousDemanda);
      }
    },
  });
};

/**
 * Mutation para comentar em demanda
 */
export const useCommentDemandMutation = (demandaId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (comentario: string) => demandaService.comentar(demandaId, comentario),

    onSuccess: () => {
      // Invalida a query para recarregar histórico/comentários
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.detail(demandaId) });
    },
  });
};

/**
 * Mutation para excluir demanda
 */
export const useDeleteDemandMutation = (demandaId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => demandaService.delete(demandaId),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: demandQueryKeys.detail(demandaId) });
      const previousDemanda = queryClient.getQueryData<Demanda>(
        demandQueryKeys.detail(demandaId)
      );

      queryClient.setQueryData<Demanda>(demandQueryKeys.detail(demandaId), (old) => {
        if (!old) return old;
        return { ...old, status: 'EXCLUIDA' } as Demanda;
      });

      return { previousDemanda };
    },

    onSuccess: () => {
      // Invalidar todas as listas de demandas
      queryClient.invalidateQueries({ queryKey: demandQueryKeys.lists() });
      // Remover a query específica da demanda excluída
      queryClient.removeQueries({ queryKey: demandQueryKeys.detail(demandaId) });
    },

    onError: (_error, _variables, context) => {
      if (context?.previousDemanda) {
        queryClient.setQueryData(demandQueryKeys.detail(demandaId), context.previousDemanda);
      }
    },
  });
};

